import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/errors';
import { toast } from '@/lib/toast';

import { sessionsService } from '../services';
import type { Session, SessionStatus } from '../types';

import { sessionsKeys } from './useSessions';

export interface UpdateSessionStatusInput {
  status: SessionStatus;
  /** Required for `status: 'cancelled'`; ignored otherwise. Max 500 chars. */
  cancellationReason?: string;
  /** Required when transitioning to `status: 'completed'` if Session.concerns is empty.
   *  BE rejects completion when no concerns exist on the row and none are supplied here. */
  concerns?: string[];
  /** Optional risk write piggy-backed on Leave & complete. BE restricts to
   *  psychologist + chief_psychologist (volunteers rejected with 403). */
  riskProfile?:
    | 'critical'
    | 'very_high'
    | 'high'
    | 'medium'
    | 'low'
    | 'very_low'
    | 'none'
    | 'unknown';
}

/**
 * Mutation hook for PATCH /sessions/:id/status. Invalidates the session
 * detail + list on success and surfaces a toast. Follows the canonical
 * pattern from useUpdateAssessmentStatus.
 *
 * Consumers remain responsible for:
 * - Permission gating (session:update_own).
 * - Confirmation UX for destructive transitions (no_show, cancelled).
 * - Collecting the cancellationReason from the user on cancel.
 */
export function useUpdateSessionStatus(id: string) {
  const qc = useQueryClient();
  return useMutation<Session, Error, UpdateSessionStatusInput>({
    mutationFn: (input) => sessionsService.updateStatus(id, input),
    onSuccess: (session) => {
      void qc.invalidateQueries({ queryKey: sessionsKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: sessionsKeys.lists() });
      toast.updated();
      return session;
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not update session status.');
    },
  });
}
